/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { DefaultOptions } from '@tanstack/query-core';
import { keepPreviousData, QueryClient } from '@tanstack/query-core';

let queryClientInstance: QueryClient | null = null;

export const getQueryClient = (): QueryClient => {
  queryClientInstance ??= new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60 * 5,
      },
    },
  });
  return queryClientInstance;
};

export const setQueryClient = (client: QueryClient): void => {
  queryClientInstance = client;
};

export const configureLibraryDefaults = (
  queryKeyPrefix: string[],
  options: DefaultOptions['queries'],
): void => {
  const client = getQueryClient();
  client.setQueryDefaults(queryKeyPrefix, options!);
};

export const createTestQueryClient = (overrides?: {
  queries?: DefaultOptions['queries'];
  mutations?: DefaultOptions['mutations'];
}): QueryClient => {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        ...overrides?.queries,
      },
      mutations: {
        retry: false,
        ...overrides?.mutations,
      },
    },
  });
  setQueryClient(client);
  return client;
};

export const resetQueryClient = (): void => {
  void queryClientInstance?.cancelQueries();
  queryClientInstance?.clear();
  queryClientInstance = null;
};
